/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { Either } from 'fp-ts/lib/Either';
import { Option } from 'fp-ts/lib/Option';
import * as io from 'io-ts';
import Collection from 'ol/Collection';
import { Coordinate } from 'ol/coordinate';
import { Extent } from 'ol/extent';
import OLFeature, { FeatureLike } from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';
import { Layer } from 'ol/layer';
import LayerGroup from 'ol/layer/Group';
import LayerTile from 'ol/layer/Tile';
import LayerVector from 'ol/layer/Vector';
import RenderFeature from 'ol/render/Feature';
import LayerRenderer from 'ol/renderer/Layer';
import Source from 'ol/source/Source';
import SourceTile from 'ol/source/Tile';
import SourceVector from 'ol/source/Vector';
import Style from 'ol/style/Style';

import type { Getter, Setter } from '../shape/types';
import type {
    DirectGeometryObject,
    Feature,
    FeatureCollection,
    GeometryType,
    ILayerInfo,
    IMapBaseLayer,
    IMapInfo,
    MessageRecord,
} from '../source';

export type TileLayer = LayerTile<SourceTile>;
export type VectorLayer = LayerVector<SourceVector<OLFeature<Geometry>>>;
export type MainLayerCollection = Collection<LayerVector | LayerGroup>;

export type Coord2D = [number, number];

// export type FeatureCollectionOrNull = FeatureCollection | null;
export type FetchData = () => Either<string, Option<FeatureCollection>>;
export type SetScaleLine = (count: number, unit: string, width: number) => void;

export interface IMapScale {
    count: number;
    unit: string;
    width: number;
}

export type ViewDirt = 'none' | 'geo' | 'geo/feature' | 'geo/extent' | 'style';

export interface IMapViewData {
    dirty: ViewDirt;
    srs: string;
    center: Coordinate;
    rotation: number;
    zoom: number;
    feature: Feature | null;
    features?: Feature[];
    extent: Extent | null;
}

export interface IGeoSelect {
    selected: string | null;
}

export interface TrackerCoordinate {
    coord: Coordinate;
    accuracy: number;
}

export interface IGeoTracker {
    track: TrackerCoordinate[];
}

export interface TrackerOptions {
    updateTrack(t: TrackerCoordinate): void;
    resetTrack(): void;
    setCenter(c: Coordinate): void;
}

export type IGeoMeasureType = 'Polygon' | 'LineString';

export interface IGeoMeasure {
    geometryType: IGeoMeasureType;
    coordinates: Coordinate[];
}

export interface MeasureOptions {
    updateMeasureCoordinates(c: Coordinate[]): void;
    stopMeasuring(): void;
    snap?: {
        layer: () => string;
        pixelTolerance: number;
    };
}

export interface ExtractFeature {
    layerId: string;
    featureId: string | number;
}

export interface ExtractOptions {
    setCollection(c: ExtractFeature[]): void;
}

export interface PositionOptions {
    setPosition(p: Coordinate): void;
    stopPosition(p: Option<Coordinate>): void;
}

export interface SingleClickOptions {
    setPosition(p: Coordinate, isActive?: boolean): void;
}

export interface IMark {
    started: boolean;
    endTime: number;
    coordinates: Coordinate;
}

export interface MarkOptions {
    endMark(): void;
    startMark(): void;
}

export type MapEditableMode = 'none' | 'select' | 'create' | 'modify';
export type MapEditableSelected = string | number | null;

export interface IGeoCreate {
    geometryType: GeometryType;
}

export interface IGeoModify {
    selected: MapEditableSelected;
    geometryType: GeometryType;
    centerOnSelected: boolean;
}

export interface FeaturePath {
    layerId: string | null;
    featureId: number | string | null;
}

export const FeaturePathIO = io.interface(
    {
        layerId: io.string,
        featureId: io.union([io.string, io.number]),
    },
    'FeaturePathIO',
);
export type NonNullFeaturePath = io.TypeOf<typeof FeaturePathIO>;

export interface FeaturePathInstance {
    layer: ILayerInfo;
    feature: Feature;
}

export type FeaturePathGetter = Getter<FeaturePath>;

export interface IPosition {
    coordinates: Coordinate;
    after: (c: Coordinate) => void;
}

export interface InteractionBase<L extends string, T> {
    label: L;
    state: T;
}

export interface InteractionSelect {
    label: 'select';
    state: IGeoSelect;
}
export interface InteractionEnter {
    label: 'enter';
    state: IGeoSelect;
}
export interface InteractionCreate {
    label: 'create';
    state: IGeoCreate;
}
export interface InteractionModify {
    label: 'modify';
    state: IGeoModify;
}
export interface InteractionTrack {
    label: 'track';
    state: IGeoTracker;
}
export interface InteractionMeasure {
    label: 'measure';
    state: IGeoMeasure;
}
export interface InteractionExtract {
    label: 'extract';
    state: ExtractFeature[];
}
export interface InteractionMark {
    label: 'mark';
    state: IMark;
}
export interface InteractionPrint {
    label: 'print';
    state: null;
}
export interface InteractionPosition {
    label: 'position';
    state: IPosition;
}
export interface InteractionSingleClick {
    label: 'singleclick';
    state: null;
}

interface InteractionMap {
    select: InteractionSelect;
    enter: InteractionEnter;
    create: InteractionCreate;
    modify: InteractionModify;
    track: InteractionTrack;
    measure: InteractionMeasure;
    extract: InteractionExtract;
    mark: InteractionMark;
    print: InteractionPrint;
    position: InteractionPosition;
    singleclick: InteractionSingleClick;
}

export type InteractionLabel = keyof InteractionMap;
export type Interaction = InteractionMap[InteractionLabel];

export type InteractionGetter = Getter<Interaction>;
export type InteractionSetter = Setter<Interaction>;

export interface EditOptions {
    getCurrentLayerId(): string;
    getGeometryType(lid: string): GeometryType;
    getStyle?: (feature: RenderFeature) => Style | Style[];
    getSnapLayer?: () => string;

    // editFeature(fid: string | number): void;
    addFeature(f: Feature): void; // create
    setGeometry(geom: DirectGeometryObject): void; // modify
}

export type FilterFunction = (
    f: FeatureLike,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    layer: Layer<Source, LayerRenderer<any>>,
) => boolean;

export interface SingleSelectOptions {
    readonly tag: 'single';
    selectFeature(lid: string, id: string | number): void;
    clearSelection(): void;
    getSelected: FeaturePathGetter;
    filter?: FilterFunction;
    getStyle?: (feature: RenderFeature) => Style | Style[];
}

export const singleSelectOptions = (
    options: Pick<
        SingleSelectOptions,
        | 'getSelected'
        | 'selectFeature'
        | 'clearSelection'
        | 'filter'
        | 'getStyle'
    >,
): SingleSelectOptions => ({
    ...options,
    tag: 'single',
});

export interface MultiSelectOptions {
    readonly tag: 'multi';
    getSelected: () => FeaturePath[] | Readonly<FeaturePath[]>;
    selectFeatures: (ps: FeaturePath[]) => void;
    filter?: FilterFunction;
    getStyle?: (feature: RenderFeature) => Style | Style[];
}

export const multiSelectOptions = (
    options: Pick<
        MultiSelectOptions,
        'getSelected' | 'selectFeatures' | 'filter' | 'getStyle'
    >,
): MultiSelectOptions => ({
    ...options,
    tag: 'multi',
});

export type SelectOptions = SingleSelectOptions | MultiSelectOptions;

export interface HighlightOptions {
    getSelected: FeaturePathGetter;
    filter?: FilterFunction;
    getStyle?: (feature: RenderFeature) => Style | Style[];
}

export interface IMapOptions {
    element: HTMLElement | null;
    getBaseLayer(): IMapBaseLayer | null;
    getView(): IMapViewData;
    getMapInfo(): IMapInfo | null;
    // todo: misleading, rename to getMapLayersInfo
    getMapLayerInfo(): ILayerInfo[];

    updateView(v: IViewEvent): void;
    setScaleLine: SetScaleLine;
    setLoading?: (ms: MessageRecord[]) => void;
}

export interface IViewEvent {
    dirty?: ViewDirt;
    center?: Coordinate;
    rotation?: number;
    zoom?: number;
    feature?: Feature;
    features?: Feature[];
    extent?: Extent;
}

export interface PrintRequest<T> {
    id: string | null;
    width: number;
    height: number;
    resolution: number;
    props: T | null;
}

export type PrintResponseStatus = 'none' | 'start' | 'end' | 'error' | 'done';

export interface PrintResponse<T> {
    id: string | null;
    status: PrintResponseStatus;
    data: string;
    extent: [number, number, number, number];
    props: T | null;
}

export interface PrintOptions<T> {
    getRequest(): PrintRequest<T>;
    setResponse(r: PrintResponse<T>): void;
}

export type ZoomLevel =
    | 0
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6
    | 7
    | 8
    | 9
    | 10
    | 11
    | 12
    | 13
    | 14
    | 15
    | 16
    | 17
    | 18
    | 19
    | 20
    | 21
    | 22
    | 23
    | 24
    | 25
    | 26
    | 27
    | 28
    | 29
    | 30;

// Calculated with commented function just above - for Lambert 72 and dpi = 96px.
export type Scale =
    | 4500000 // TODO : check this value
    | 2000000
    | 1000000
    | 550000
    | 250000
    | 150000
    | 70000
    | 35000
    | 20000
    | 8500
    | 4500
    | 2000
    | 1000
    | 550
    | 300
    | 150
    | 65
    | 35
    | 15
    | 8
    | 4
    | 2
    | 1
    | 0.5
    | 0.3
    | 0.1
    | 0.05
    | 0.03
    | 0.02
    | 0.008
    | 0.004;
